import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UniqueId from 'uniqid';
import { Icon, USER_INTERFACE_TICK_DONE } from '@bwoty-web/styleguide';
import { classificationItemPropType } from '../../utils/propTypes';
import { debounce } from '../../utils/helpers';
import CarouselItem from './carouselItem/carouselItem';
import CarouselNavArrow from './carouselNavArrow/carouselNavArrow';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import './carouselPicker.scss';

const SCROLL_INTERVAL = 350;

const CarouselPicker = ({
  options,
  selected,
  onSelect,
  fullSize,
  limit,
  allowToggleAll,
  selectAllLabel,
  hideNavArrows,
}) => {
  const isDesktop = useIsDesktop();
  const [showNavArrows, setShowNavArrows] = useState(hideNavArrows ? false : isDesktop);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [selectAllSelected, setSelectAllSelected] = useState(false);
  const carouselContainerId = UniqueId('carousel-picker-');

  useEffect(() => {
    if (allowToggleAll) {
      const allSelected = Object.keys(options).every(optionKey => selected.includes(optionKey))
      setSelectAllSelected(allSelected);
    }
  }, [allowToggleAll, selected, options])

  useEffect(() => {
    if (isDesktop) {
      const containerEl = document.getElementById(carouselContainerId);
      const { offsetWidth, offsetLeft } = containerEl;
      const availableContainerSpace = offsetWidth + offsetLeft;
      const carouselItem = document.querySelectorAll(`#${carouselContainerId} .carousel-item`)[0];

      const styles = window.getComputedStyle(carouselItem)
      const { marginLeft, marginRight } = styles
  
      const itemWidth = carouselItem.offsetWidth + parseFloat(marginLeft) + parseFloat(marginRight);
      const numberOfItems = Object.keys(options).length + (allowToggleAll ? 1 : 0) // + 1 for the select all option
      const totalItemWidth = itemWidth * numberOfItems;
      setShowNavArrows(hideNavArrows ? false : totalItemWidth > availableContainerSpace)
    } else {
      setShowNavArrows(false);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isDesktop])

  const scrollToItem = (el) => {
    const containerEl = document.getElementById(carouselContainerId);
    const { scrollLeft, offsetWidth } = containerEl;
    
    if (el.offsetLeft > scrollLeft && el.offsetLeft + el.offsetWidth < scrollLeft + offsetWidth) {
      return
    }
    
    const elementRightSidePos = el.offsetLeft + el.offsetWidth
    const containerCurrentRightPos = offsetWidth + scrollLeft
    const outOfBoundsRight = elementRightSidePos > containerCurrentRightPos;

    const scrollPadding = 75
    let scrollPos = el.offsetLeft
    // outOfBoundsRight - meaning is the clicked item to the right and partially out of the scroll view.
    // If it is, scroll the container so the clicked item is placed on the containers ride side + some scrollpadding
    // Otherwise just scroll to the left side + scrollpadding. We do this so that when you click an item it's scrolled
    // into view so that the user clearly sees that the item has been selected/unselected
    if (outOfBoundsRight) {
      scrollPos = (el.offsetLeft + el.offsetWidth + scrollPadding) - containerEl.offsetWidth;
    } else {
      scrollPos = el.offsetLeft - scrollPadding;
    }

    containerEl.scroll({
      left: scrollPos,
      behavior: 'smooth',
    })
  }

  const handleSelect = (id, el) => {
    const alreadySelected = selected?.includes(id)
    
    if (!limit || selected?.length < limit || alreadySelected) {
      onSelect(id)
    }
  
    // el is not defined when "Select all" is clicked
    if (el) {
      scrollToItem(el);
      if (selected?.length === limit && !alreadySelected) {
        el.classList.add('bounce');
        setTimeout(() => {
          el.classList.remove('bounce');
        }, 200);
      }
    }
  }

  /* debounce this to not fire hundreds of events when scrolling */
  const handleCarouselScroll = debounce(() => {
    const el = document.getElementById(carouselContainerId);
    if (!el) return;

    const { scrollLeft, scrollWidth, offsetWidth } = el;
    if ((scrollLeft + offsetWidth) >= scrollWidth) {
      setCanScrollRight(false);
    } else {
      setCanScrollRight(true);
    }

    if (scrollLeft > 0) {
      setCanScrollLeft(true);
    } else {
      setCanScrollLeft(false);
    }
  }, 20);

  const toggleAll = () => {
    Object.keys(options).forEach((optionKey) => {
      // Always unselect or select all. If some a already selected, don't toggle these.
      if ((selected.includes(optionKey) && selectAllSelected) || !selected.includes(optionKey) && !selectAllSelected) {
        handleSelect(optionKey);
        setSelectAllSelected(!selectAllSelected);
      }
    })
  }

  const renderToggleAll = () => {
    return (
      <div className='carousel-item carousel-picker__select-all__container'
        role='button'
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.keycode === 13) {
            toggleAll()
          }
        }}
        onClick={toggleAll}>

        <div className={`carousel-picker__select-all__box ${selectAllSelected ? 'active' : ''}`} data-test={`select-all-button${selectAllSelected ? '-selected':''}`}>
          <Icon
            fill='#fff'
            className='carousel-picker__select-all__box__check'
            path={USER_INTERFACE_TICK_DONE}
            size="l"
          />
        </div>
          <p className='carousel-picker__select-all__label'>{selectAllLabel}</p>
      </div>
    );
  }

  const renderFade = (position, visible) => {
    const visibleClassSuffix = visible ? 'visible' : 'hidden';
    return <div className={`carousel-picker__edge-fade carousel-picker__edge-fade--${position} carousel-picker__edge-fade--${visibleClassSuffix}`} />
  }

  return (
    <div onScroll={(e) => handleCarouselScroll(e)} className={`carousel-picker ${fullSize ? 'carousel-picker--full-size' : ''}`}>

      { showNavArrows && (
        <CarouselNavArrow
          active={canScrollLeft}
          setLeftActiveState={setCanScrollLeft}
          setRightActiveState={setCanScrollRight}
          scrollInterval={SCROLL_INTERVAL}
          direction='left'
          carouselContainerId={carouselContainerId} /> 
      )}

      { isDesktop && renderFade('left', canScrollLeft) }
      
      <div id={carouselContainerId} className={`carousel-picker__container ${fullSize ? 'carousel-picker__container--full-size' : ''}`}>
        { allowToggleAll && renderToggleAll() }

        { Object.keys(options).map(optionKey => {
          const option = options[optionKey];
          return (
            <CarouselItem
              fullSize={fullSize}
              key={optionKey}
              selected={selected.includes(optionKey)}
              onSelect={handleSelect}
              id={optionKey}
              displayName={option.displayName}
              image={option.image} />
          )
        })}
      </div>
      
      { isDesktop && renderFade('right', canScrollRight) }

      { showNavArrows && (
        <CarouselNavArrow
          active={canScrollRight}
          setLeftActiveState={setCanScrollLeft}
          setRightActiveState={setCanScrollRight}
          scrollInterval={SCROLL_INTERVAL}
          direction='right'
          carouselContainerId={carouselContainerId} />
      )}
    </div>
  )
}

CarouselPicker.defaultProps = {
  fullSize: true,
  limit: null,
  allowToggleAll: false,
  selectAllLabel: null,
  hideNavArrows: false,
};

CarouselPicker.propTypes = {
  options: PropTypes.objectOf(classificationItemPropType).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func.isRequired,
  fullSize: PropTypes.bool,
  limit: PropTypes.number,
  allowToggleAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  hideNavArrows: PropTypes.bool,
};

export default CarouselPicker;
